import React, { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiCalendar, HiMail } from 'react-icons/hi';
import Button from './ui/Button/Button';
import { useGeneral } from '../contexts';
import { useErrorPage } from '../contexts/ErrorPageContext';
import SEO from './SEO';
import styles from './ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if error is related to logout or authentication
    const isAuthError = 
      error.message?.includes('Logout') ||
      error.message?.includes('authentication') ||
      error.message?.includes('token') ||
      error.message?.includes('unauthorized');

    if (isAuthError) {
      // Clear any stale auth data
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      } catch (e) {
        // Error clearing storage
      }
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Use fallback UI if provided
      if (this.props.fallback) {
        return (
          <>
            {this.props.fallback}
          </>
        );
      }

      // Default error UI - ErrorBoundary replaces entire content so Header won't show
      return (
        <ErrorFallback 
          error={this.state.error} 
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

// Functional component wrapper to use hooks
const ErrorFallback: React.FC<{ error: Error | null; onReset: () => void }> = ({ error, onReset }) => {
  const navigate = useNavigate();
  const { language } = useGeneral();
  const { getTranslation } = language;
  const { setIsErrorPage } = useErrorPage();

  useEffect(() => {
    setIsErrorPage(true);
    return () => setIsErrorPage(false);
  }, [setIsErrorPage]);

  const handleGoHome = () => {
    onReset();
    navigate('/');
  };

  const handleExploreEvents = () => {
    navigate('/events');
  };

  const handleExploreLodos = () => {
    window.open('https://lodos.io', '_blank');
  };

  const handleContactUs = () => {
    window.location.href = 'mailto:info@lodos.io';
  };

  return (
    <>
      <SEO
        title={getTranslation('errorBoundary.title') || 'Something went wrong'}
        description={
          getTranslation('errorBoundary.description') ||
          'An unexpected error occurred. Please try again or contact support if the problem persists.'
        }
        url={typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : undefined}
        noindex
        lang={language.language}
      />
      <main className={styles.content}>
        <div className={styles.container}>
        <h1 className={styles.title}>500</h1>
        <h2 className={styles.subtitle}>
          {getTranslation('errorBoundary.title') || 'Something went wrong'}
        </h2>
        <p className={styles.description}>
          {getTranslation('errorBoundary.description') || 
            'An unexpected error occurred. Please try again or contact support if the problem persists.'}
        </p>
        {error && (
          <div className={styles.errorMessage}>
            {error.message || getTranslation('errorBoundary.unexpectedError') || 'An unexpected error occurred'}
          </div>
        )}
        
        <div className={styles.actions}>
          <Button 
            variant="primary" 
            size="medium"
            onClick={handleGoHome}
          >
            {getTranslation('errorBoundary.goHome') || 'Go Home'}
          </Button>
        </div>

        <div className={styles.sections}>
          <div className={styles.sectionCard} onClick={handleExploreEvents}>
            <div className={`${styles.icon} ${styles.iconOrange}`}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiCalendar size={32} />
            </div>
            <h3 className={styles.sectionTitle}>
              {getTranslation('notFound.exploreEvents') || 'Explore Events'}
            </h3>
            <p className={styles.sectionSubtitle}>
              {getTranslation('notFound.exploreEventsSubtitle') || "Let's join"}
            </p>
          </div>

          <div className={styles.sectionCard} onClick={handleExploreLodos}>
            <div className={styles.lodosIconWrapper}>
              <img src="/lodos-favicon.png" alt="Lodos" className={styles.lodosIcon} />
            </div>
            <h3 className={styles.sectionTitle}>
              {getTranslation('notFound.exploreLodos') || 'Explore Lodos'}
            </h3>
            <p className={styles.sectionSubtitle}>
              {getTranslation('notFound.exploreLodosSubtitle') || 'Connect people'}
            </p>
          </div>

          <div className={styles.sectionCard} onClick={handleContactUs}>
            <div className={`${styles.icon} ${styles.iconGreen}`}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiMail size={32} />
            </div>
            <h3 className={styles.sectionTitle}>
              {getTranslation('notFound.contactUs') || 'Contact Us'}
            </h3>
            <p className={styles.sectionSubtitle}>
              {getTranslation('notFound.contactUsSubtitle') || 'Ask a question'}
            </p>
          </div>
        </div>
        </div>
      </main>
    </>
  );
};

// Wrapper component that provides navigate context
const ErrorBoundary: React.FC<Props> = (props) => {
  return <ErrorBoundaryClass {...props} />;
};

export default ErrorBoundary;

